// Picking on the plan (docs/claude/selection.md, *The pick stack*): what a press
// takes, worked out from the hits under the cursor, the layer stacking and one
// pickability rule — so the click rules are unit-tested, not only clicked in a
// browser. The per-kind hit tests (which need the view and the drawing's
// geometry) stay with main.ts `objectStackAt`; everything after them is here:
//  - `pickable` is THE rule for what a click can reach (not frozen, not on a
//    hidden or locked layer) — it was written three ways;
//  - `frontPick` is THE click rule over what lies under the pointer, in every
//    view (the plan, a section view, 3D): the front one, or nothing when it is
//    locked; Tab through every pickable one;
//  - `refRank` says where a hit paints (a room's features in their slots);
//  - `orderPickStack` puts the hits in the order a press takes them;
//  - `reachesRoom` / `reachesTable` say whether a press gets through to a
//    room or a schedule table under what else is there;
//  - `clickTake` is what a click on the head selects — the press, the
//    right-click, the hover, a section view and Tab all ask it.
import type {
  Connection,
  Drawing,
} from '../shared/types';
import { clickTarget } from './groups';
import type { FeatureKind } from './selection';
import {
  type ItemKind,
  type LayerModel,
  onHiddenLayer,
  onLockedLayer,
} from './layers';

/** A room's feature as the pick stack holds it: every kind a room's feature can
 *  be selected as (selection.ts `FeatureSel`), so a new one is in the stack — and
 *  reached by the hover, Tab and the right-click — or does not compile. */
export type RefFeature = FeatureKind;
const REF_FEATURES: Record<RefFeature, true> = { bench: true, door: true, fixture: true, glass: true, heaterWall: true, innerWall: true, opening: true, support: true };
/** One object in the drawing, by kind — what the pick stack holds: a drawing
 *  object by its ItemKind (a schedule table is never in it) or a room's feature. */
export interface ObjRef {
  kind: Exclude<ItemKind, 'table'> | RefFeature;
  id: string;
  /** The room of a room's feature (a bench, pane, opening, door, fixture,
   *  heater wall, partition or bench support). */
  instId?: string;
  /** A bench support's run (the row it stands in). */
  run?: number;
}
export const refKey = (r: ObjRef): string => `${r.kind}:${r.id}`;
/** The drawing-object kind a ref names; null for a room's feature. */
export function refItem(kind: ObjRef['kind']): ItemKind | null {
  return kind in REF_FEATURES ? null : (kind as ItemKind);
}

/** THE rule for what a click can reach: an object that is not frozen and not on
 *  a hidden or locked layer (a locked one is drawn and snapped to, never picked). */
export function pickable(m: LayerModel, id: string, frozen = false): boolean {
  return !frozen && !onHiddenLayer(m, id) && !onLockedLayer(m, id);
}

/** What lies under the pointer by THE click rule (docs/claude/selection.md, *A
 *  locked object in front*), in every view: the plan's stack, a section view's
 *  and the 3D view's (both through viewPicks.ts `frontPicks`: main.ts
 *  `sectionPicksAt`, ui/view3d.ts `picks3dAt`). */
export interface FrontPick<T> {
  /** What a plain click takes, and what the hover outlines: the FRONT one —
   *  none over nothing, nor when the front one is locked (`blocked`). */
  head: T | null;
  /** What Tab / Alt+click cycle through: every pickable one, front first, so
   *  they reach what a locked one stands in front of. */
  tab: T[];
  /** A locked one is in front: a click takes nothing and clears the selection
   *  (not blank space: there a section view's click goes back to the view). */
  blocked: boolean;
}
/** THE click rule over `seen` — what lies under the pointer, front first, each
 *  as what a click on it takes, or null for one SEEN there that no click may
 *  take (frozen, on a locked layer). What you see in front is what you click: a
 *  locked one in front takes nothing. What is hidden is not seen: never in
 *  `seen`, it never blocks. */
export function frontPick<T>(seen: readonly (T | null)[]): FrontPick<T> {
  const head = seen.length ? seen[0] : null;
  return { head, tab: seen.filter((t): t is T => t !== null), blocked: seen.length > 0 && head === null };
}

/** Where a ref paints in the layer stacking (higher = on top): an object's own
 *  rank; a room's bench, backrest, door, heater wall, partition or support just
 *  above the room (in the order a press takes them), its panes and openings
 *  after what stands in it (they paint there). -1 = not painted. */
export function refRank(r: ObjRef, m: LayerModel): number {
  const base = m.rank.get(r.instId ?? r.id);
  if (base === undefined) return -1;
  if (r.kind === 'glass' || r.kind === 'opening') return m.roomTop.get(r.instId!) ?? base + 0.5;
  if (r.kind === 'support') return base + 0.4;
  if (r.kind === 'heaterWall' || r.kind === 'innerWall') return base + 0.35;
  if (r.kind === 'door') return base + 0.3;
  if (r.kind === 'bench') return base + 0.25;
  if (r.kind === 'fixture') return base + 0.27;
  return base;
}

/** The plan's stack: what a click takes and Tab cycles (`frontPick`), and
 *  `seen` — every object there, a locked one too — which `reachesRoom` /
 *  `reachesTable` read, so a locked part over a room's door keeps the press off
 *  the door as it keeps it off the room. */
export interface PickStack extends FrontPick<ObjRef> { seen: ObjRef[] }

/**
 * The hits under the cursor in the order a Select press takes them: each once,
 * what is hidden left out, what is DRAWN ON TOP first (a tie keeps the order the
 * hits came in: a room's bench before the room). A connection yields to the
 * objects it joins: it is drawn over them, but its ends run into them, and a
 * press there means the part — otherwise a connected part could not be grabbed
 * by its middle. Then the click rule (`frontPick`): the head is what a plain
 * click takes — none when what is on top is locked (`frozen`: an instance
 * frozen in place) —, the pickable ones are what Tab / Alt+click cycle to. A
 * part standing in a locked room ranks ahead of it, so it is still taken.
 */
export function orderPickStack(hits: readonly ObjRef[], m: LayerModel, connections: readonly Pick<Connection, 'from' | 'id' | 'to'>[], frozen: (id: string) => boolean = () => false): PickStack {
  const seen = new Set<string>();
  const sorted = hits
    .filter((r) => { const k = refKey(r); if (seen.has(k)) return false; seen.add(k); return !onHiddenLayer(m, r.instId ?? r.id); })
    .map((r, i) => ({ r, i, k: refRank(r, m) }))
    .sort((a, b) => b.k - a.k || a.i - b.i)
    .map((x) => x.r);
  for (const r of [...sorted]) {
    if (r.kind !== 'conn') continue;
    const c = connections.find((x) => x.id === r.id);
    if (!c) continue;
    const at = sorted.indexOf(r);
    let last = -1;
    sorted.forEach((o, i) => { if (o.kind !== 'conn' && (o.id === c.from || o.id === c.to)) last = Math.max(last, i); });
    if (last > at) { sorted.splice(at, 1); sorted.splice(last, 0, r); }
  }
  const can = (r: ObjRef): boolean => { const id = r.instId ?? r.id; return pickable(m, id, frozen(id)); };
  return { ...frontPick(sorted.map((r) => (can(r) ? r : null))), seen: sorted };
}

/** The front one of the refs `want` takes (Shift+click: the kinds a selection
 *  mixes, past a dimension over them) — none when a locked one is in front of it. */
export function frontWhere(stack: PickStack, want: (r: ObjRef) => boolean): ObjRef | null {
  const ok = new Set(stack.tab.map(refKey));
  for (const r of stack.seen) {
    if (!ok.has(refKey(r))) return null;
    if (want(r)) return r;
  }
  return null;
}

/** Can a press reach this room (or one of its features)? Not on a hidden or
 *  locked layer, nor when anything else painted above it is ahead of it in the
 *  stack — `seen`, a locked one too — (a connection yields to the room it
 *  joins, a pane paints over its contents). `own`: the rank the press is painted
 *  at (a mark in the room's top slot sits over what stands in it). */
export function reachesRoom(m: LayerModel, stack: readonly ObjRef[], roomId: string, own = refRank({ kind: 'instance', id: roomId }, m)): boolean {
  if (!pickable(m, roomId)) return false;
  const at = stack.findIndex((r) => (r.instId ?? r.id) === roomId);
  const ahead = at < 0 ? stack : stack.slice(0, at);
  return !ahead.some((r) => (r.instId ?? r.id) !== roomId && refRank(r, m) > own);
}

/** Can a press reach a schedule table? Not on a hidden or locked layer, nor
 *  under anything painted above it (the stack's `seen`, a locked one too). */
export function reachesTable(m: LayerModel, stack: readonly ObjRef[], tableId: string): boolean {
  if (!pickable(m, tableId)) return false;
  const own = m.rank.get(tableId) ?? -1;
  return !stack.some((r) => refRank(r, m) > own);
}

/** What a click selects (docs/claude/selection.md, *What a click takes*): the
 *  head itself (`ref`: a room's feature with its room, or an object); a group's
 *  member, its outermost group (`ids`); a member of a group already selected,
 *  one level in (`drill`: the press keeps the selection and its release drills,
 *  a section view's click takes it at once). `exact` (Tab, Alt+click): the head
 *  itself, group or not. */
export type ClickTake = { ref: ObjRef } | { ids: string[] } | { drill: string[] };
export function clickTake(d: Drawing, head: ObjRef, selected: ReadonlySet<string>, exact = false): ClickTake {
  if (exact || !refItem(head.kind)) return { ref: head };
  const t = clickTarget(d, head.id, selected);
  if (t?.now) return { ids: t.now };
  if (t?.drill) return { drill: t.drill };
  return { ref: head };
}
